import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import FontFaceObserver from 'fontfaceobserver';
import { useUpdateShape } from 'hooks';
import SceneHelper from 'utils/SceneHelper';
import { fitWeekdayNames } from 'utils/fitWeekdayNames';

/**
 * Fits the active calendar after its selected header font has loaded.
 *
 * @param {import('actions/scene.types').CalendarShape} shape
 * @param {ReturnType<typeof import('constants/calendarSettings').getCalendarSettings>} calendarSettings
 * @returns {void}
 */
const useFitWeekdayNames = (shape, calendarSettings) => {
  const updateShape = useUpdateShape(shape.id);
  const isCurrentPageShape = useSelector((state) =>
    SceneHelper.getCurrentPageShapes(state).some((currentShape) => currentShape.id === shape.id),
  );
  const { width, height, margin } = calendarSettings.WeekdayNameShape;
  const { left, right, top, bottom } = margin;
  const fontSize = Number(shape.headerFontSize) || 10;
  const fontFamily = shape.headerFontFamily || shape.fontFamily || 'Raleway';
  const weight = shape.headerIsBold ? '700' : '400';
  const style = shape.headerIsItalic ? 'italic' : 'normal';
  const dayFormat = shape.dayFormat || 'EEEEEE';

  useEffect(() => {
    if (!isCurrentPageShape) return undefined;
    let cancelled = false;
    const font = new FontFaceObserver(fontFamily, { weight, style });
    font
      .load(null, 10000)
      .then(() => {
        if (cancelled) return;
        const context = document.createElement('canvas').getContext('2d');
        if (!context) return;
        const fittedSize = fitWeekdayNames({
          fontSize,
          width: Math.max(0, width - left - right),
          height: Math.max(0, height - top - bottom),
          dayFormat,
          measureText: (text, size) => {
            context.font = `${style} ${weight} ${size}px ${JSON.stringify(fontFamily)}`;
            return context.measureText(text).width;
          },
        });
        if (fittedSize < fontSize) {
          updateShape({
            headerFontSize: fittedSize,
            weekdayNamePadding: { left, right, top, bottom },
          });
        }
      })
      .catch(() => {
        // Keep the requested size if loading fails; never fit against a fallback font.
      });
    return () => {
      cancelled = true;
    };
  }, [
    isCurrentPageShape,
    fontFamily,
    weight,
    style,
    fontSize,
    dayFormat,
    width,
    height,
    left,
    right,
    top,
    bottom,
    updateShape,
  ]);
};

export default useFitWeekdayNames;
